/*
  AutoReminderSettings.jsx
  Config screen for auto-sending Email/WhatsApp payment reminders on overdue invoices.
*/
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  BellRing, Save, Loader2, Play, Eye, Mail, MessageCircle,
  CheckCircle, XCircle, Clock, FileClock, TrendingDown,
} from "lucide-react";

const DEFAULT_RULE = () => ({
  days_overdue: 0, name: "New Rule", tone: "gentle",
  subject: "Reminder: Invoice {invoice_no}",
  body_html: "<p>Dear {client_name},</p><p>Invoice {invoice_no} for ₹ {total} is due on {due_date}.</p>",
});

const TONES = [
  { value: "gentle", label: "Gentle", color: "text-emerald-600 bg-emerald-50" },
  { value: "follow_up", label: "Follow-up", color: "text-blue-600 bg-blue-50" },
  { value: "urgent", label: "Urgent", color: "text-amber-600 bg-amber-50" },
  { value: "final", label: "Final Notice", color: "text-red-600 bg-red-50" },
];

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function AutoReminderSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/collections/invoices/reminders/config");
      setCfg(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/collections/invoices/reminders/config", cfg);
      setCfg(r.data);
      toast.success("Auto-reminder config saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const runPreview = async () => {
    try {
      const r = await api.get("/collections/invoices/reminders/preview");
      setPreviewData(r.data);
      setShowPreview(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const runNow = async () => {
    if (!window.confirm("Trigger reminder run right now? This will actually send emails / WhatsApp messages.")) return;
    setRunning(true);
    try {
      const r = await api.post("/collections/invoices/reminders/run?dry_run=false");
      toast.success(`Sent ${r.data.sent_email} email(s), ${r.data.sent_whatsapp} WhatsApp — ${r.data.skipped} skipped, ${r.data.failed} failed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    setRunning(false);
  };

  const loadLogs = async () => {
    try {
      const r = await api.get("/collections/invoices/reminders/logs?limit=100");
      setLogs(r.data || []);
      setShowLogs(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  };

  const setField = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const setRule = (i, patch) => setCfg(c => ({
    ...c, rules: c.rules.map((r, idx) => idx === i ? { ...r, ...patch } : r),
  }));
  const addRule = () => setCfg(c => ({ ...c, rules: [...(c.rules || []), DEFAULT_RULE()] }));
  const removeRule = (i) => setCfg(c => ({ ...c, rules: c.rules.filter((_, idx) => idx !== i) }));

  const toggleChannel = (ch) => {
    setCfg(c => ({ ...c, channels: c.channels?.includes(ch) ? c.channels.filter(x => x !== ch) : [...(c.channels || []), ch] }));
  };

  if (loading || !cfg) {
    return <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;
  }

  return (
    <Card data-testid="auto-reminder-panel" className={saving ? "opacity-70 pointer-events-none transition-opacity" : "transition-opacity"}>
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 pb-3 border-b">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${cfg.enabled ? "bg-emerald-100" : "bg-slate-100"}`}>
              <BellRing className={`w-5 h-5 ${cfg.enabled ? "text-emerald-600" : "text-slate-400"}`} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Invoice Auto-Reminders</h3>
              <p className="text-[10.5px] text-slate-500 max-w-md">Automatic Email / WhatsApp payment reminders for overdue Tax invoices — runs daily on your schedule.</p>
              <div className="flex items-center gap-2 mt-1.5">
                <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setField("enabled", v)} data-testid="ar-enabled-switch" />
                <span className={`text-[11px] font-semibold ${cfg.enabled ? "text-emerald-600" : "text-slate-500"}`}>
                  {cfg.enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={runPreview} className="gap-1.5" data-testid="ar-preview-btn"><Eye className="w-3.5 h-3.5" /> Preview Today</Button>
            <Button variant="outline" size="sm" onClick={loadLogs} className="gap-1.5" data-testid="ar-logs-btn"><FileClock className="w-3.5 h-3.5" /> Logs</Button>
            <Button size="sm" onClick={runNow} disabled={running} className="gap-1.5 bg-amber-500 hover:bg-amber-600" data-testid="ar-run-now-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run Now
            </Button>
          </div>
        </div>

        {/* Schedule + Channels + Dedupe */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">Daily Run Time (IST)</Label>
            <Input type="time" value={cfg.run_time || "09:00"} onChange={e => setField("run_time", e.target.value)} className="h-9 text-xs" data-testid="ar-run-time" />
            <p className="text-[10px] text-slate-400 mt-0.5">Scheduler kicks off at this local time.</p>
          </div>
          <div>
            <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">Min Gap Between Reminders</Label>
            <div className="flex items-center gap-1"><Input type="number" min="1" max="60" value={cfg.min_gap_days ?? 3} onChange={e => setField("min_gap_days", Number(e.target.value))} className="h-9 text-xs" data-testid="ar-min-gap" /><span className="text-xs text-slate-500">days</span></div>
          </div>
          <div>
            <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">Stop After (Overdue)</Label>
            <div className="flex items-center gap-1"><Input type="number" min="1" max="365" value={cfg.max_overdue_days ?? 90} onChange={e => setField("max_overdue_days", Number(e.target.value))} className="h-9 text-xs" data-testid="ar-max-overdue" /><span className="text-xs text-slate-500">days</span></div>
          </div>
          <div>
            <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">Channels</Label>
            <div className="flex gap-1.5 mt-1">
              <button type="button" onClick={() => toggleChannel("email")} className={`px-3 py-1.5 rounded text-[11px] font-semibold border flex items-center gap-1 ${cfg.channels?.includes("email") ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-500"}`} data-testid="ar-ch-email"><Mail className="w-3 h-3" /> Email</button>
              <button type="button" onClick={() => toggleChannel("whatsapp")} className={`px-3 py-1.5 rounded text-[11px] font-semibold border flex items-center gap-1 ${cfg.channels?.includes("whatsapp") ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-200 text-slate-500"}`} data-testid="ar-ch-wa"><MessageCircle className="w-3 h-3" /> WhatsApp</button>
            </div>
          </div>
        </section>

        {cfg.channels?.includes("whatsapp") && (
          <section className="bg-emerald-50/60 border border-emerald-100 rounded p-3">
            <p className="text-[11px] font-semibold text-emerald-900 mb-2">WhatsApp Template (Meta approved)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-[10.5px]">Template Name</Label>
                <Input value={cfg.whatsapp_template_name || ""} onChange={e => setField("whatsapp_template_name", e.target.value)} placeholder="invoice_reminder" className="h-9 text-xs" data-testid="ar-wa-template" />
              </div>
              <div>
                <Label className="text-[10.5px]">Language Code</Label>
                <Input value={cfg.whatsapp_lang_code || "en_US"} onChange={e => setField("whatsapp_lang_code", e.target.value)} className="h-9 text-xs" data-testid="ar-wa-lang" />
              </div>
            </div>
            <p className="text-[10px] text-emerald-800 mt-2">Template variables (in order): <code>customer_name</code>, <code>invoice_no</code>, <code>amount</code>, <code>days_overdue</code>.</p>
          </section>
        )}

        {/* Reminder ladder */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="text-xs font-bold text-slate-700">Reminder Ladder</Label>
              <p className="text-[10.5px] text-slate-500">Each rule fires when an invoice hits its <em>days-overdue</em> threshold (latest applicable rule wins).</p>
            </div>
            <Button size="sm" variant="outline" onClick={addRule} className="h-7 text-xs" data-testid="ar-add-rule">+ Add Step</Button>
          </div>
          <div className="space-y-3">
            {(cfg.rules || []).sort((a, b) => (a.days_overdue || 0) - (b.days_overdue || 0)).map((r, i) => {
              const tone = TONES.find(t => t.value === r.tone) || TONES[0];
              return (
                <div key={i} className="border rounded-lg p-3 bg-white shadow-sm" data-testid={`ar-rule-${i}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${tone.color}`}>{tone.label}</span>
                    <span className="text-[11px] text-slate-500">Step #{i + 1}</span>
                    <div className="flex items-center gap-1 ml-auto">
                      <Label className="text-[10px]">Days Overdue</Label>
                      <Input type="number" value={r.days_overdue} onChange={e => setRule(i, { days_overdue: Number(e.target.value) })} className="h-7 w-20 text-xs" data-testid={`ar-rule-days-${i}`} />
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeRule(i)} data-testid={`ar-rule-remove-${i}`}><XCircle className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <div><Label className="text-[10px]">Rule Name</Label><Input value={r.name} onChange={e => setRule(i, { name: e.target.value })} className="h-8 text-xs" /></div>
                    <div>
                      <Label className="text-[10px]">Tone</Label>
                      <select value={r.tone} onChange={e => setRule(i, { tone: e.target.value })} className="w-full h-8 border rounded px-2 text-xs bg-white">
                        {TONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="mb-2"><Label className="text-[10px]">Email Subject</Label><Input value={r.subject} onChange={e => setRule(i, { subject: e.target.value })} className="h-8 text-xs" data-testid={`ar-rule-subject-${i}`} /></div>
                  <div><Label className="text-[10px]">Email Body (HTML — supports {"{invoice_no}, {client_name}, {total}, {due_date}, {days_overdue}, {issuer_name}"})</Label>
                    <Textarea value={r.body_html} onChange={e => setRule(i, { body_html: e.target.value })} rows={4} className="text-[11px] font-mono" data-testid={`ar-rule-body-${i}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="flex justify-end pt-3 border-t">
          <Button onClick={save} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="ar-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Config
          </Button>
        </div>
      </CardContent>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[820px]" data-testid="ar-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4 text-blue-600" /> Preview — Invoices That Will Get Reminders Today</DialogTitle>
          </DialogHeader>
          {previewData ? (
            <>
              <div className="text-[11px] text-slate-500 mb-2">
                <strong>{previewData.count}</strong> candidate invoice(s) · <strong className="text-emerald-600">{previewData.will_send}</strong> will send · <strong className="text-slate-400">{previewData.count - previewData.will_send}</strong> will be skipped (dedupe).
              </div>
              <div className="overflow-x-auto max-h-[65vh]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-slate-50 text-slate-500 text-[10px] uppercase">
                      <th className="text-left py-2 px-2">Invoice #</th>
                      <th className="text-left py-2 px-2">Customer</th>
                      <th className="text-right py-2 px-2">Amount</th>
                      <th className="text-right py-2 px-2">Due</th>
                      <th className="text-right py-2 px-2">Overdue</th>
                      <th className="text-left py-2 px-2">Rule</th>
                      <th className="text-center py-2 px-2">Send?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.items.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400 italic">No overdue invoices today.</td></tr>}
                    {previewData.items.map(it => (
                      <tr key={it.invoice_id} className="border-b">
                        <td className="py-1.5 px-2 font-mono">{it.invoice_no}</td>
                        <td className="py-1.5 px-2">{it.client_name}</td>
                        <td className="py-1.5 px-2 text-right font-mono">₹ {Number(it.total || 0).toLocaleString("en-IN")}</td>
                        <td className="py-1.5 px-2 text-right">{it.due_date}</td>
                        <td className="py-1.5 px-2 text-right">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${it.days_overdue > 15 ? "bg-red-100 text-red-700" : it.days_overdue > 7 ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{it.days_overdue}d</span>
                        </td>
                        <td className="py-1.5 px-2 text-slate-600">{it.rule_name}</td>
                        <td className="py-1.5 px-2 text-center">
                          {it.will_send
                            ? <CheckCircle className="w-4 h-4 text-emerald-600 inline" />
                            : <span title={it.skip_reason}><Clock className="w-4 h-4 text-slate-400 inline" /></span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : <div className="py-8 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-[820px]" data-testid="ar-logs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileClock className="w-4 h-4 text-slate-600" /> Recent Reminder Activity</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto max-h-[65vh]">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-slate-50 text-slate-500 text-[10px] uppercase">
                  <th className="text-left py-2 px-2">When</th>
                  <th className="text-left py-2 px-2">Invoice</th>
                  <th className="text-left py-2 px-2">Channel</th>
                  <th className="text-left py-2 px-2">To</th>
                  <th className="text-left py-2 px-2">Rule</th>
                  <th className="text-center py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400 italic">No reminder history yet.</td></tr>}
                {logs.map(l => (
                  <tr key={l.id} className="border-b">
                    <td className="py-1.5 px-2 text-slate-500">{fmtDT(l.sent_at)}</td>
                    <td className="py-1.5 px-2 font-mono">{l.invoice_no || "—"}</td>
                    <td className="py-1.5 px-2 capitalize">
                      {l.channel === "email" ? <span className="inline-flex items-center gap-1 text-blue-600"><Mail className="w-3 h-3" /> Email</span>
                        : <span className="inline-flex items-center gap-1 text-emerald-600"><MessageCircle className="w-3 h-3" /> WhatsApp</span>}
                    </td>
                    <td className="py-1.5 px-2 text-slate-600">{l.to}</td>
                    <td className="py-1.5 px-2 text-slate-500">{l.rule_name}</td>
                    <td className="py-1.5 px-2 text-center">
                      {l.status === "sent"
                        ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle className="w-3 h-3" /> Sent</span>
                        : <span className="inline-flex items-center gap-1 text-red-600" title={l.error}><TrendingDown className="w-3 h-3" /> Failed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
